import { useState } from "react";

import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Snackbar,
  TextField,
} from "@mui/material";

import { useDisclosure } from "hooks";

import { ShipmentTemplateListElement } from "./components/ShipmentTemplateListElement";

import type { ShipmentTemplate } from "types/shipment";

// Longer than the other menu messages, since it names the template and recipients.
const SHARE_CONFIRMATION_AUTO_HIDE_MS = 3000;

interface Props {
  error: string | null;
  loading: boolean;
  templates: ShipmentTemplate[];
  onTemplateClicked: (template: ShipmentTemplate) => void;
  selectedTemplateId: number | null;
  /** Opens the list and flashes this template, e.g. one just shared with the user. */
  highlightedTemplateId: number | null;
  onDeleteTemplate: (template: ShipmentTemplate) => void;
  onShareTemplate: (template: ShipmentTemplate) => void;
  shareConfirmation: string | null;
  onCloseShareConfirmation: VoidFunction;
  deletedTemplate: boolean;
  onCloseDeletedTemplate: VoidFunction;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
}

export function ShipmentTemplateMenu(props: Props) {
  const {
    error,
    loading,
    templates,
    onTemplateClicked,
    selectedTemplateId,
    highlightedTemplateId,
    onDeleteTemplate,
    onShareTemplate,
    shareConfirmation,
    onCloseShareConfirmation,
    deletedTemplate,
    onCloseDeletedTemplate,
    searchQuery,
    onSearchQueryChange,
  } = props;

  const {
    isOpen: appliedOpen,
    onOpen: onOpenApplied,
    onClose: onCloseApplied,
  } = useDisclosure(false);

  const [listOpen, setListOpen] = useState(false);

  // Open the list when a highlight arrives, adjusting state during render
  // rather than in an effect.
  const [previousHighlightedTemplateId, setPreviousHighlightedTemplateId] =
    useState<number | null>(null);
  if (highlightedTemplateId !== previousHighlightedTemplateId) {
    setPreviousHighlightedTemplateId(highlightedTemplateId);
    if (highlightedTemplateId != null) setListOpen(true);
  }

  return (
    <Box>
      <Autocomplete
        open={listOpen}
        onOpen={() => setListOpen(true)}
        onClose={() => setListOpen(false)}
        options={templates}
        filterOptions={(x) => x}
        getOptionLabel={(t) => t.name}
        value={null}
        // A template is applied once and never recalculated, so it must not be
        // selectable until everything it is resolved against has arrived.
        disabled={loading}
        inputValue={searchQuery}
        onInputChange={(_, value, reason) => {
          if (reason === "input" || reason === "clear")
            onSearchQueryChange(value);
        }}
        onChange={(_, template) => {
          if (template) {
            onTemplateClicked(template);
            onSearchQueryChange("");
            onOpenApplied();
          }
        }}
        loading={loading}
        loadingText="Loading templates..."
        noOptionsText={error ?? "No templates found."}
        openOnFocus
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select a template..."
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <SearchIcon sx={{ color: "text.secondary", mr: 0.5 }} />
              ),
              endAdornment: loading ? (
                <CircularProgress size={20} />
              ) : (
                params.InputProps.endAdornment
              ),
            }}
          />
        )}
        renderOption={(optionProps, template) => {
          const { key, ...liProps } = optionProps;
          return (
            <ShipmentTemplateListElement
              key={key ?? template.id}
              liProps={liProps}
              template={template}
              selectedTemplateId={selectedTemplateId}
              highlighted={highlightedTemplateId === template.id}
              onDeleteTemplate={onDeleteTemplate}
              onShareTemplate={onShareTemplate}
            />
          );
        }}
      />
      <Snackbar
        open={appliedOpen}
        autoHideDuration={2000}
        onClose={onCloseApplied}
      >
        <Alert
          onClose={onCloseApplied}
          severity="success"
          sx={{ width: "100%" }}
        >
          Template Applied!
        </Alert>
      </Snackbar>
      <Snackbar
        open={shareConfirmation !== null}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={SHARE_CONFIRMATION_AUTO_HIDE_MS}
        onClose={onCloseShareConfirmation}
      >
        <Alert
          onClose={onCloseShareConfirmation}
          severity="success"
          sx={{ width: "100%" }}
        >
          {shareConfirmation}
        </Alert>
      </Snackbar>
      <Snackbar
        open={deletedTemplate}
        autoHideDuration={2000}
        onClose={onCloseDeletedTemplate}
      >
        <Alert
          onClose={onCloseDeletedTemplate}
          severity="success"
          sx={{ width: "100%" }}
        >
          Template Deleted
        </Alert>
      </Snackbar>
    </Box>
  );
}
